import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { QuickView } from "@/components/QuickView";

const SIZES = ["S", "M", "L", "XL", "30", "32", "34", "36", "Free Size"];
const SORTS = [
  { v: "newest", l: "Newest" },
  { v: "price_low", l: "Price: Low to High" },
  { v: "price_high", l: "Price: High to Low" },
  { v: "name", l: "Name A–Z" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quick, setQuick] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(params.get("category") || "All");
  const [size, setSize] = useState("");
  const [availability, setAvailability] = useState("");
  const [newOnly, setNewOnly] = useState(params.get("new") === "1");
  const [maxPrice, setMaxPrice] = useState(15000);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setCategory(params.get("category") || "All");
    setNewOnly(params.get("new") === "1");
  }, [params]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const qs = new URLSearchParams();
      if (search) qs.set("search", search);
      if (category && category !== "All") qs.set("category", category);
      if (size) qs.set("size", size);
      if (availability) qs.set("availability", availability);
      if (newOnly) qs.set("newArrivals", "true");
      qs.set("maxPrice", String(maxPrice));
      qs.set("sort", sort);
      api
        .get(`/products?${qs.toString()}`)
        .then(({ data }) => setProducts(data))
        .catch(() => setProducts([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, category, size, availability, newOnly, maxPrice, sort]);

  const activeFilters =
    (category !== "All" ? 1 : 0) + (size ? 1 : 0) + (availability ? 1 : 0) + (newOnly ? 1 : 0);

  const reset = () => {
    setSearch("");
    setCategory("All");
    setSize("");
    setAvailability("");
    setNewOnly(false);
    setMaxPrice(15000);
    setParams({});
  };

  const FilterPanel = () => (
    <div className="space-y-8" data-testid="filter-panel">
      <div>
        <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-4">Category</p>
        <div className="flex flex-col gap-1">
          {["All", ...categories.map((c) => c.name)].map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`text-left py-1.5 text-sm transition-colors ${category === c ? "text-white" : "text-neutral-500 hover:text-neutral-300"}`}
              data-testid={`filter-category-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-4">Size</p>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button
              key={s}
              onClick={() => setSize(size === s ? "" : s)}
              className={`min-w-9 h-9 px-2 border font-mono-th text-xs ${size === s ? "bg-white text-black border-white" : "border-white/15 text-neutral-400 hover:border-white/40"}`}
              data-testid={`filter-size-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-4">Max Price: ₹{maxPrice.toLocaleString("en-IN")}</p>
        <input
          type="range"
          min="1000"
          max="15000"
          step="500"
          value={maxPrice}
          onChange={(e) => setMaxPrice(Number(e.target.value))}
          className="w-full accent-white"
          data-testid="filter-price"
        />
      </div>

      <div>
        <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-4">Availability</p>
        <div className="flex flex-col gap-1">
          {[
            { v: "", l: "All" },
            { v: "in_stock", l: "In Stock" },
            { v: "sold_out", l: "Sold Out" },
          ].map((a) => (
            <button key={a.v} onClick={() => setAvailability(a.v)} className={`text-left py-1.5 text-sm ${availability === a.v ? "text-white" : "text-neutral-500 hover:text-neutral-300"}`} data-testid={`filter-availability-${a.v || "all"}`}>
              {a.l}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-3 cursor-pointer" data-testid="filter-new-arrivals">
        <input type="checkbox" checked={newOnly} onChange={(e) => setNewOnly(e.target.checked)} className="w-4 h-4 accent-white" />
        <span className="text-sm text-neutral-300">New Arrivals only</span>
      </label>

      {activeFilters > 0 && (
        <button onClick={reset} className="font-mono-th text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white underline" data-testid="filter-reset">
          Clear all filters
        </button>
      )}
    </div>
  );

  return (
    <div className="pt-28 md:pt-36 pb-20 min-h-screen" data-testid="shop-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="mb-10">
          <p className="font-mono-th text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">The Collection</p>
          <h1 className="font-display font-black uppercase text-5xl md:text-7xl tracking-tight">Shop</h1>
        </div>

        {/* Search + sort bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-10">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search garments..."
              className="w-full bg-[#13151A] border border-white/10 pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-white/30"
              data-testid="shop-search"
            />
          </div>
          <div className="flex gap-4">
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#13151A] border border-white/10 px-4 py-3.5 text-sm focus:outline-none focus:border-white/30 font-mono-th uppercase text-xs tracking-wider" data-testid="shop-sort">
              {SORTS.map((s) => (
                <option key={s.v} value={s.v} className="bg-[#13151A]">{s.l}</option>
              ))}
            </select>
            <button onClick={() => setShowFilters(true)} className="lg:hidden flex items-center gap-2 border border-white/15 px-4 py-3.5 font-mono-th text-xs uppercase tracking-wider" data-testid="shop-filter-toggle">
              <SlidersHorizontal className="w-4 h-4" /> Filters {activeFilters > 0 && `(${activeFilters})`}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-10">
          <aside className="hidden lg:block">
            <FilterPanel />
          </aside>

          <div>
            <p className="font-mono-th text-xs text-neutral-500 mb-6" data-testid="shop-result-count">{products.length} products</p>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[3/4] bg-[#1A1D24] animate-pulse" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="py-32 text-center" data-testid="shop-empty">
                <p className="font-display text-2xl text-neutral-400">No products found</p>
                <p className="text-neutral-600 mt-2 text-sm">Try adjusting your filters.</p>
                <button onClick={reset} className="mt-6 font-mono-th text-xs uppercase tracking-[0.2em] border border-white/20 px-6 py-3 hover:bg-white/5">Reset filters</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {products.map((p, i) => (
                  <ProductCard key={p.id} product={p} onQuickView={setQuick} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-[70] lg:hidden" data-testid="mobile-filters">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowFilters(false)} />
          <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm glass p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <span className="font-display font-bold uppercase tracking-[0.15em]">Filters</span>
              <button onClick={() => setShowFilters(false)} data-testid="mobile-filters-close"><X className="w-6 h-6" /></button>
            </div>
            <FilterPanel />
            <button onClick={() => setShowFilters(false)} className="mt-10 w-full bg-white text-black py-4 font-mono-th text-xs uppercase tracking-[0.2em]">Show Results</button>
          </motion.div>
        </div>
      )}

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
}
